import { useTranslation } from "react-i18next";
import { Form } from "semantic-ui-react";

import "components/DBB/components/DBBCreateContractModal/components/DBBCustomerSection/DBBCustomerSection.scss";
import { createFormHelper } from "lib/Form";
import { CUSTOMER_TYPES } from "models";
import { getCustomerOrCompanyName, getCustomerTypeLabel } from "modules/Appointments/components/NewAppointment/utils";

import type { DropdownItemProps } from "components";
import type { DBBContractFormPayload, DBBContractSection } from "components/DBB/components/DBBCreateContractModal/components/DBBCreateContractForm";
import type { TFunction } from "i18next";
import type { Customer } from "models";
import type { CustomerSection } from "modules/Appointments/components/NewAppointment";
import type { CarCustomerInfo } from "modules/CarDetails/hooks";

export type DBBContractCustomer = CustomerSection & {
  name: string;
  number: string;
};

type DBBCustomerSectionProps = {
  section: DBBContractSection;
  customers?: CarCustomerInfo;
  hasValidationError: boolean;
};

const FormHelper = createFormHelper<DBBContractFormPayload>();

export const DBB_MANDATORY_CUSTOMER_FIELDS: (keyof DBBContractCustomer)[] = ["street", "number", "postcode", "place"];

export const getTrimmedValue = (value?: string) => value?.trim() ?? "";

export const hasContractCustomerName = (customer?: DBBContractCustomer) => !!(getTrimmedValue(customer?.company) || getTrimmedValue(customer?.name));

const CUSTOMER_ROLES = [
  { customerType: CUSTOMER_TYPES.Driver, infoField: "driver" },
  { customerType: CUSTOMER_TYPES.Owner, infoField: "owner" },
  { customerType: CUSTOMER_TYPES.Contractor, infoField: "contractor" }
] as const;

export const toContractCustomer = (customer?: Customer, customerType?: CUSTOMER_TYPES): DBBContractCustomer | undefined => {
  if (!customer) return;

  return { ...customer, customer_type: customerType ?? customer.customer_type, name: getCustomerOrCompanyName(customer), number: customer.house_nr ?? "" };
};

const getCustomerRoleOptions = (t: TFunction, customers?: CarCustomerInfo) =>
  CUSTOMER_ROLES.reduce<DropdownItemProps<CUSTOMER_TYPES>[]>((options, { customerType, infoField }) => {
    const customer = customers?.[infoField];
    if (customer) options.push({ key: customerType, value: customerType, text: getCustomerOrCompanyName(customer), description: getCustomerTypeLabel(t, customerType) });

    return options;
  }, []);

const getRoleCustomer = (customers?: CarCustomerInfo, customerType?: CUSTOMER_TYPES) => {
  const role = CUSTOMER_ROLES.find(customerRole => customerRole.customerType === customerType);

  return role && customers?.[role.infoField];
};

export const DBBCustomerSection = ({ section, customers, hasValidationError }: DBBCustomerSectionProps) => {
  const { t } = useTranslation();
  const { watch, setValue } = FormHelper.useFormContext();

  const isUnknown = watch(section.unknownField);
  const customer = watch(section.customerField);

  const isMandatory = (field: keyof DBBContractCustomer) => DBB_MANDATORY_CUSTOMER_FIELDS.includes(field);
  const hasMandatoryError = (field: keyof DBBContractCustomer) => hasValidationError && isMandatory(field) && !customer?.[field];
  const hasNameError = hasValidationError && !hasContractCustomerName(customer);

  return (
    <div className="customer-section">
      <FormHelper.Field.Checkbox
        name={section.unknownField}
        label={t("v8_unknown", "Unknown")}
        onChange={isChecked => isChecked && setValue(section.customerField, undefined)}
      />

      {!isUnknown && (
        <>
          <Form.Group>
            <Form.Field width={8}>
              <label>{t("v8_claire_customer_type", "Claire Customer type")}</label>
              <FormHelper.Field.Dropdown
                name={`${section.customerField}.customer_type`}
                options={getCustomerRoleOptions(t, customers)}
                onChange={customerType => setValue(section.customerField, toContractCustomer(getRoleCustomer(customers, customerType), customerType))}
                dropdownProps={{ selectOnBlur: false, placeholder: t("v8_customer_type", "Customer type") }}
              />
            </Form.Field>
            <Form.Field width={8} required={!getTrimmedValue(customer?.name)} error={hasNameError}>
              <label>{t("v8_company", "Company")}</label>
              <FormHelper.Field.Input name={`${section.customerField}.company`} inputProps={{ placeholder: t("v8_company", "Company") }} />
            </Form.Field>
          </Form.Group>
          <Form.Group>
            <Form.Field width={8}>
              <label>{t("v8_initials", "Initials")}</label>
              <FormHelper.Field.Input name={`${section.customerField}.initials`} inputProps={{ placeholder: t("v8_initials", "Initials") }} />
            </Form.Field>
            <Form.Field width={8} required={!getTrimmedValue(customer?.company)} error={hasNameError}>
              <label>{t("v8_name", "Name")}</label>
              <FormHelper.Field.Input name={`${section.customerField}.name`} inputProps={{ placeholder: t("v8_name", "Name") }} />
            </Form.Field>
          </Form.Group>
          <Form.Group>
            <Form.Field width={8} required={isMandatory("street")} error={hasMandatoryError("street")}>
              <label>{t("v8_street", "Street")}</label>
              <FormHelper.Field.Input name={`${section.customerField}.street`} inputProps={{ placeholder: t("v8_street", "Street") }} />
            </Form.Field>
            <Form.Field width={8} required={isMandatory("number")} error={hasMandatoryError("number")}>
              <label>{t("v8_number", "Number")}</label>
              <FormHelper.Field.Input name={`${section.customerField}.number`} inputProps={{ placeholder: t("v8_number", "Number") }} />
            </Form.Field>
          </Form.Group>
          <Form.Group>
            <Form.Field width={8} required={isMandatory("postcode")} error={hasMandatoryError("postcode")}>
              <label>{t("v8_postcode", "Postcode")}</label>
              <FormHelper.Field.Input name={`${section.customerField}.postcode`} inputProps={{ placeholder: t("v8_postcode", "Postcode") }} />
            </Form.Field>
            <Form.Field width={8} required={isMandatory("place")} error={hasMandatoryError("place")}>
              <label>{t("v8_city", "City")}</label>
              <FormHelper.Field.Input name={`${section.customerField}.place`} inputProps={{ placeholder: t("v8_city", "City") }} />
            </Form.Field>
          </Form.Group>
          <Form.Group>
            <Form.Field width={6}>
              <label>{t("v8_email", "Email")}</label>
              <FormHelper.Field.Input name={`${section.customerField}.email_business`} inputProps={{ placeholder: t("v8_email", "Email") }} />
            </Form.Field>
            <Form.Field width={6}>
              <label>{t("v8_mobile", "Mobile")}</label>
              <FormHelper.Field.Input name={`${section.customerField}.tel_mobile_business`} inputProps={{ placeholder: t("v8_mobile", "Mobile") }} />
            </Form.Field>
            <Form.Field width={6}>
              <label>{t("v8_phone", "Phone")}</label>
              <FormHelper.Field.Input name={`${section.customerField}.tel_business_nr`} inputProps={{ placeholder: t("v8_phone", "Phone") }} />
            </Form.Field>
          </Form.Group>
        </>
      )}
    </div>
  );
};
